import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TeamsAutoAttendantsViewResource } from '../resources/teams-auto-attendants-view.resource';
import { combineLatest, defer, Observable, of, Subscription } from 'rxjs';
import { BreadcrumbsService } from '../../shared/breadcrumbs/breadcrumbs.service';
import {
  AutoAttendantHoliday,
  AutoAttendantOperatorType,
  AutoAttendantsFieldConfig,
  CallRoutingOption,
  MicrosoftTeamsAutoAttendantView,
  UserPhoto,
} from '../../shared/models/generated/ziroModels';
import { ButtonSizes, ButtonStyles } from '../../button/button.component';
import { ZiroIcons } from '../../shared/models/ziro-icons.enum';
import { ZpmUserPhotoContext } from '../../shared/contexts/zpm-user-photo.context';
import { finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { TeamsAutoAttendantsFieldConfigResource } from '../resources/teams-auto-attendants-field-config.resource';
import { TeamsAutoAttendantsHolidayResource } from '../resources/teams-auto-attendants-holiday.resource';
import { TeamsAutoAttendantEditContext } from '../teams-auto-attendant-edit.context';
import { AutoAttendantGeneralSettingsEntity } from './teams-auto-attendant-edit-form.component';
import { transformEnumToHumanReadableLabel } from '../auto-attendants-util';
import { ZiroSelectOption } from '../../forms/fields/select/ziro-select.component';
import { TeamsAutoAttendantsService } from '../teams-auto-attendants.service';
import { UiCallFlow } from './call-flows/call-flows-side-nav/call-flows-side-nav.component';

@Component({
  selector: 'ziro-teams-auto-attendant-edit',
  templateUrl: './teams-auto-attendant-edit.component.html',
  styleUrl: './teams-auto-attendant-edit.component.scss',
  standalone: false,
  providers: [
    TeamsAutoAttendantsViewResource,
    TeamsAutoAttendantsFieldConfigResource,
    TeamsAutoAttendantsService,
    TeamsAutoAttendantsHolidayResource,
    TeamsAutoAttendantEditContext,
  ],
})
export class TeamsAutoAttendantEditComponent implements OnInit, OnDestroy {
  private _autoAttendantsFieldConfigResource = inject(TeamsAutoAttendantsFieldConfigResource);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _editContext = inject(TeamsAutoAttendantEditContext);
  private _route = inject(ActivatedRoute);
  private _teamsAutoAttendantsHolidayResource = inject(TeamsAutoAttendantsHolidayResource);
  private _teamsAutoAttendantsViewResource = inject(TeamsAutoAttendantsViewResource);
  private _teamsAutoAttendantService = inject(TeamsAutoAttendantsService);
  private _zpmUserPhotoContext = inject(ZpmUserPhotoContext);

  autoAttendantId: string;
  autoAttendantView: MicrosoftTeamsAutoAttendantView;
  generalSettingsEntity: AutoAttendantGeneralSettingsEntity;
  is360ViewChild = false;
  tenantId: string;
  autoAttendantsFieldConfig: AutoAttendantsFieldConfig;
  autoAttendantsHolidays: AutoAttendantHoliday[];
  userPhotos: UserPhoto[] = [];

  isLoading = signal(true);
  isSaving = signal(false);

  private _subscriptions = new Subscription();
  private _upn: string;

  ngOnInit(): void {
    this._initRouteData();

    const sub = this._initComponent$()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe();
    this._subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    if (!this.is360ViewChild) {
      this._breadcrumbsService.clearBreadcrumbs();
    }
  }

  private _initRouteData(): void {
    this.autoAttendantId = this._route.snapshot.paramMap.get('autoAttendantId');
    this.tenantId = this._route.snapshot.paramMap.get('tenantId');
    this.is360ViewChild = this._route.snapshot.data['is360ViewChild'];
    this._upn = this._route.snapshot.params.upn;
  }

  private _initComponent$(): Observable<void> {
    return defer(() => (!this.autoAttendantId ? this._initNewAutoAttendant$() : this._initExistingAutoAttendant$()));
  }

  private _initNewAutoAttendant$(): Observable<void> {
    return combineLatest([
      this._autoAttendantsFieldConfigResource.get(this.tenantId),
      this._teamsAutoAttendantsHolidayResource.getAllHolidays(this.tenantId),
    ]).pipe(
      tap(([fieldConfig, holidays]) => {
        this.autoAttendantsFieldConfig = {
          languages: fieldConfig.languages.sort((a, b) => a.displayName.localeCompare(b.displayName)),
          timeZones: fieldConfig.timeZones,
        };
        this.autoAttendantsHolidays = holidays;

        this.generalSettingsEntity = this._buildNewGeneralSettingsEntity();
        this.autoAttendantView = this._buildNewAutoAttendantView();
        this._editContext.initNew(holidays);
        this._initBreadcrumbs('');
      }),
      switchMap(() => of(null)),
    );
  }

  private _initExistingAutoAttendant$(): Observable<void> {
    return combineLatest([
      this._teamsAutoAttendantsViewResource.getAutoAttendantView(this.tenantId, this.autoAttendantId),
      this._autoAttendantsFieldConfigResource.get(this.tenantId),
      this._teamsAutoAttendantsHolidayResource.getAllHolidays(this.tenantId),
    ]).pipe(
      switchMap(([autoAttendantView, fieldConfig, holidays]) =>
        this._loadUserPhotos$(autoAttendantView).pipe(
          map((userPhotos) => ({
            autoAttendantView,
            fieldConfig,
            holidays,
            userPhotos,
          })),
        ),
      ),
      tap(({ autoAttendantView, fieldConfig, holidays, userPhotos }) => {
        this.autoAttendantsFieldConfig = {
          languages: fieldConfig.languages.sort((a, b) => a.displayName.localeCompare(b.displayName)),
          timeZones: fieldConfig.timeZones,
        };
        this.autoAttendantsHolidays = holidays;
        this.autoAttendantView = autoAttendantView;
        this.userPhotos = userPhotos;

        this.generalSettingsEntity = this._toGeneralSettingsEntity(autoAttendantView);
        this._editContext.initExisting(
          autoAttendantView,
          this._toStandardCallFlows(autoAttendantView),
          this._toHolidayCallFlows(autoAttendantView),
          holidays,
        );

        this._initBreadcrumbs(autoAttendantView.name);
      }),
      switchMap(() => of(null)),
    );
  }

  private _loadUserPhotos$(autoAttendantView: MicrosoftTeamsAutoAttendantView): Observable<UserPhoto[]> {
    const validUpns = autoAttendantView.users.map((user) => user.upn).filter((upn): upn is string => !!upn);

    return this._zpmUserPhotoContext.initAutoAttendantUserPhotos(this.tenantId, validUpns).pipe(
      switchMap(() => this._zpmUserPhotoContext.state$.pipe(take(1))),
      map(() => this._zpmUserPhotoContext.getUserPhotos()),
    );
  }

  private _buildNewGeneralSettingsEntity(): AutoAttendantGeneralSettingsEntity {
    return {
      name: '',
      destination: null,
      language: null,
      timezone: null,
      operatorType: null,
      resourceAccounts: [],
    };
  }

  private _buildNewAutoAttendantView(): MicrosoftTeamsAutoAttendantView {
    return {
      id: null,
      name: '',
      language: '',
      teamsPhoneAgentEnabled: false,
      timeZone: '',
      operator: null,
      users: [],
      resourceAccounts: [],
      dialScopeInclusionGroups: [],
      dialScopeExclusionGroups: [],
      businessHours: null,
      businessHoursCallFlow: {
        callRoutingOption: CallRoutingOption.DISCONNECT,
        greetingOption: null,
        name: '',
        playMenuOptionSettings: null,
        redirectCallSettings: null,
      },
      afterHoursCallFlow: null,
      holidayCallFlows: [],
    };
  }

  private _toGeneralSettingsEntity(
    autoAttendantView: MicrosoftTeamsAutoAttendantView,
  ): AutoAttendantGeneralSettingsEntity {
    const timezone = this.autoAttendantsFieldConfig.timeZones.find(
      (fieldConfigTimeZone) => fieldConfigTimeZone.id === autoAttendantView.timeZone,
    );
    const language = this.autoAttendantsFieldConfig.languages.find(
      (fieldConfigLanguage) => fieldConfigLanguage.code === autoAttendantView.language,
    );

    return {
      name: autoAttendantView.name,
      destination: this._getOperatorDestination(autoAttendantView),
      language: {
        label: language?.displayName ?? autoAttendantView.language,
        value: language?.code ?? autoAttendantView.language,
      },
      timezone: {
        label: timezone?.displayName ?? autoAttendantView.timeZone,
        value: timezone?.id ?? autoAttendantView.timeZone,
      },
      operatorType: this._getOperatorType(autoAttendantView),
      resourceAccounts: autoAttendantView.resourceAccounts.map((account) => {
        return { label: account.displayName, value: account.guid };
      }),
    };
  }

  private _toStandardCallFlows(autoAttendantView: MicrosoftTeamsAutoAttendantView): UiCallFlow[] {
    const businessHoursFlows = [autoAttendantView.businessHoursCallFlow]
      .filter((flow): flow is NonNullable<typeof flow> => !!flow)
      .map((flow) => ({
        id: crypto.randomUUID(),
        schedule: autoAttendantView.businessHours ?? null,
        redirectCallSettingsEntity: flow.redirectCallSettings?.entity,
        ...flow,
      }));

    const afterHoursFlows = [autoAttendantView.afterHoursCallFlow]
      .filter((flow): flow is NonNullable<typeof flow> => !!flow)
      .map((flow) => ({
        id: crypto.randomUUID(),
        schedule: autoAttendantView.businessHours ?? null,
        isAfterHoursCallFlow: true,
        redirectCallSettingsEntity: flow.redirectCallSettings?.entity,
        ...flow,
      }));

    const afterHoursEntry: UiCallFlow[] =
      afterHoursFlows.length > 0
        ? afterHoursFlows
        : [
            {
              id: crypto.randomUUID(),
              name: 'After Hours',
              isAfterHoursCallFlow: true,
              schedule: null,
              callRoutingOption: CallRoutingOption.DISCONNECT,
              greetingOption: null,
              playMenuOptionSettings: null,
              redirectCallSettings: null,
              redirectCallSettingsEntity: null,
            },
          ];

    return [...businessHoursFlows, ...afterHoursEntry];
  }

  private _toHolidayCallFlows(autoAttendantView: MicrosoftTeamsAutoAttendantView): UiCallFlow[] {
    return [
      ...(autoAttendantView.holidayCallFlows ?? []).map((holidayFlow) => ({
        id: crypto.randomUUID(),
        isHoliday: true,
        holiday: holidayFlow.schedule,
        ...holidayFlow.callFlow,
        redirectCallSettingsEntity: holidayFlow.callFlow.redirectCallSettings?.entity,
      })),
    ];
  }

  private _initBreadcrumbs(name: string): void {
    if (!this.is360ViewChild) {
      this._breadcrumbsService.updateBreadcrumbs([
        {
          label: 'tkey;admin.teams_voice_management.teams_auto_attendants.title',
          routerLink: true,
          url: `/auto-attendant-management/tenants/${this.tenantId}/teams-auto-attendants`,
        },
        {
          label: name,
        },
      ]);
    } else {
      this._breadcrumbsService.updateBreadcrumbs([
        {
          label: 'tkey;microsoft_360.view.breadcrumb.home.text',
          url: `/helpdesk/tenants/${this.tenantId}/360-view/${this._upn}`,
          routerLink: true,
        },
        {
          label: 'tkey;microsoft_360.view.auto_attendants.breadcrumb.text',
        },
        {
          label: name,
        },
      ]);
    }
  }

  private _getOperatorDestination(autoAttendantView: MicrosoftTeamsAutoAttendantView): ZiroSelectOption {
    const { operator } = autoAttendantView;
    if (!operator) {
      return { label: '', value: '' };
    }
    switch (operator.type) {
      case AutoAttendantOperatorType.EXTERNAL_PHONE_NUMBER:
        return {
          label: this._teamsAutoAttendantService.formatNumberFromApi(autoAttendantView.operator.destination),
          value: autoAttendantView.operator.destination,
        };
      case AutoAttendantOperatorType.APPLICATION_ENDPOINT:
      case AutoAttendantOperatorType.USER:
      case AutoAttendantOperatorType.CONFIGURATION_ENDPOINT:
        return { label: operator.entity.displayName, value: operator.entity.displayName };

      case AutoAttendantOperatorType.NOT_SUPPORTED:
      default:
        return { label: '', value: '' };
    }
  }

  private _getOperatorType(autoAttendantView: MicrosoftTeamsAutoAttendantView): ZiroSelectOption {
    const { operator } = autoAttendantView;
    if (!operator) {
      return {
        label: 'None',
        value: AutoAttendantOperatorType.NOT_SUPPORTED,
      };
    }
    switch (operator.type) {
      case AutoAttendantOperatorType.APPLICATION_ENDPOINT:
      case AutoAttendantOperatorType.USER:
      case AutoAttendantOperatorType.CONFIGURATION_ENDPOINT:
        return { label: transformEnumToHumanReadableLabel(operator.entity.type), value: operator.type };
      case AutoAttendantOperatorType.EXTERNAL_PHONE_NUMBER:
      case AutoAttendantOperatorType.NOT_SUPPORTED:
      default:
        return { label: transformEnumToHumanReadableLabel(operator.type), value: operator.type };
    }
  }

  protected readonly ZiroIcons = ZiroIcons;
  protected readonly ButtonSizes = ButtonSizes;
  protected readonly ButtonStyles = ButtonStyles;
}
